import React, { PropsWithChildren, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { useLanguageMenuItem } from '@hooks/i18n/useLanguageMenuItem.tsx';
import { MenuItemModel } from '@components/menu/types.ts';
import clsx from 'clsx';
import AppSidebar from '@app/layout/AppSidebar.tsx';
import useSidebarState from '@app/layout/useSidebarState.tsx';
import Customer2InheritorNotificationSidebar
  from '@components/ringil3/Features/Customer2InheritorNotification/sidebars/Customer2InheritorNotificationSidebar.tsx';
import SvgMenuGlobe from '@app/icons/menu/MenuGlobe.tsx';
import useLockDocumentScroll from '@hooks/useLockDocumentScroll/useLockDocumentScroll.tsx';
import useShellMetrics from '@hooks/useShellMetrics/useShellMetrics.tsx';
import { CONTENT_SCROLL_ATTR } from '@hooks/useScrollBehindPanel/useScrollBehindPanel.tsx';

interface ComponentProps {
  languageMenuAfterInfo?: boolean;
}

const PublicLayout: React.FC<PropsWithChildren<ComponentProps>> = ({ children, languageMenuAfterInfo = false }) => {
  // The shell below is exactly one viewport tall and scrolls internally — the document must never scroll too.
  useLockDocumentScroll();
  const sidebar = useSidebarState();
  const isMobile = sidebar.isMobile;

  const { contentRef } = useShellMetrics();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { tr } = useTranslationLgs();
  const languageMenuItem = useLanguageMenuItem();

  const [visible, setVisible] = useState(false);

  // Function to get mobile menu items
  const getMobileMenuItems = () => {
    const items = [];

    // Dashboard
    items.push({
      icon: <SvgMenuGlobe />,
      label: tr('PublicLayout.MenuItemMoreAboutRingil', 'More about Ringil'),
      onClick: () => window.open('https://ringil.com', '_blank'),
      show: true,
      isActive: false,
    });

    return items;
  };

  const appMenu: MenuItemModel[] = [
    {
      title: tr('PublicLayout.MenuItemMoreAboutRingil', 'More about Ringil'),
      icon: <SvgMenuGlobe />,
      url: 'https://ringil.com',
      render: true,
    },
  ] as MenuItemModel[];

  const userMenu = [languageMenuItem] as MenuItemModel[];

  return (
    <>
      <div className={'h-screen flex flex-col md:flex-row bg-base-tertiary'}>
        {isMobile && (
          <div className="fixed bottom-0 left-0 right-0 bg-white z-20 py-3 px-4 border-t flex justify-between items-center shadow-lg">
            {getMobileMenuItems().map(
              (item, index) =>
                item.show && (
                  <button
                    key={index}
                    className={`flex flex-col items-center justify-center text-xs w-20 border-none bg-transparent ${item.isActive ? 'text-[#FE5000]' : 'text-gray-600'}`}
                    onClick={item.onClick}
                  >
                    {item.icon}
                    <span className="mt-1">{item.label}</span>
                  </button>
                ),
            )}
          </div>
        )}

        <AppSidebar
          state={sidebar}
          menu={appMenu}
          userMenu={userMenu}
          userMenuInline={languageMenuAfterInfo}
          menuStorageKey={'Layout.publicMenuOpen1'}
          userMenuStorageKey={'Layout.publicMenuOpen2'}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
          panelClassName={'bg-base-secondary'}
          headerSlot={({ smallMenu }) => (
            <div
              // The blurb has no rail form — it would reflow into a one-word column, so it fades out.
              className={clsx('px-4 py-3 shrink-0 w-[270px] transition-opacity duration-150 ease-in-out motion-reduce:transition-none', {
                'opacity-0 pointer-events-none': smallMenu,
              })}
              style={{
                color: '#616061',
                fontFamily: 'DM Sans',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              {tr('PublicLayout.ringilText', 'Ringil is a system for complete logistics management in manufacturing and sales companies')}
            </div>
          )}
          bottomSlot={() => (
            <>
              {!languageMenuAfterInfo && <hr className={'bg-base-base h-[1px] border-0'} />}
              <Customer2InheritorNotificationSidebar setVisible={v => setVisible(v)} visible={visible} />
            </>
          )}
        />
        <main className={clsx('relative flex-1 flex overflow-hidden w-full min-w-0', { 'pb-16': isMobile })}>
          <div
            ref={contentRef}
            className={clsx('flex-1 min-w-0 max-w-[1550px] pl-0.5 pb-0.5 pr-0.5 gap-2 flex-col overflow-y-scroll', {
              'ml-[var(--app-gutter,24px)] mr-[var(--app-gutter,24px)]': !isMobile,
            })}
            {...{ [CONTENT_SCROLL_ATTR]: true }}
          >
            {isMobile && mobileMenuOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity duration-150" onClick={() => setMobileMenuOpen(false)}></div>
            )}
            {children}
          </div>
        </main>
      </div>
    </>
  );
};

export default PublicLayout;
